import React, { useState, useRef } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Upload, FileText, Loader2, AlertCircle, CheckCircle2, 
  X, ChevronDown, ChevronUp, Info, Eye, ChevronRight, Lock,
  Sparkles, Users, Package, ListChecks, Camera, GitCompareArrows
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { API_BASE } from '../config/api';
import RevisionDiff from './RevisionDiff';

const API = API_BASE;

const MAX_SCRIPT_FILE_SIZE_MB = 10;
const MAX_SCRIPT_FILE_SIZE_BYTES = MAX_SCRIPT_FILE_SIZE_MB * 1024 * 1024;

const ScriptImport = ({ filmId, onImportComplete, disabled = false, freeSceneCap = null, autoExpand = false }) => {
  const { openUpgradeModal } = useWorkspace();
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedScenes, setSelectedScenes] = useState({});
  const [isExpanded, setIsExpanded] = useState(autoExpand && !disabled);
  const [expandedPreviews, setExpandedPreviews] = useState({});
  const [viewingScene, setViewingScene] = useState(null);
  // AI mode — default ON for Pro users. Falls back to regex parser if AI fails.
  const [useAI, setUseAI] = useState(true);
  const [aiUsed, setAiUsed] = useState(false); // true after successful AI parse
  // M2.4 revision re-import: how many scenes the film already has, the diff, and the applied result
  const [existingCount, setExistingCount] = useState(0);
  const [diff, setDiff] = useState(null);
  const [diffing, setDiffing] = useState(false);
  const [revisionResult, setRevisionResult] = useState(null);
  const fileInputRef = useRef(null);

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return;
    if (selectedFile.size > MAX_SCRIPT_FILE_SIZE_BYTES) {
      setError(`File is too large. Maximum allowed size is ${MAX_SCRIPT_FILE_SIZE_MB}MB.`);
      return;
    }
    setFile(selectedFile);
    setParseResult(null);
    setImportResult(null);
    setError(null);
    setSelectedScenes({});
    setExpandedPreviews({});
  };

  const handleFileSelect = (e) => {
    validateAndSetFile(e.target.files?.[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    validateAndSetFile(e.dataTransfer.files?.[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const parseScript = async () => {
    if (!file) return;
    
    setParsing(true);
    setError(null);
    setParseResult(null);
    setImportResult(null);
    setAiUsed(false);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // NOTE: Do NOT set Content-Type manually when sending FormData.
      // The browser must set it so the correct `boundary=...` parameter is included,
      // otherwise FastAPI's python-multipart parser cannot decode the body (→ 422).

      // Try AI parse first if enabled; fall back to regex if AI fails.
      let response;
      let usedAI = false;
      if (useAI) {
        try {
          response = await axios.post(
            `${API}/films/${filmId}/script/ai-parse`,
            formData,
            { headers: getAuthHeaders(), timeout: 120000 }
          );
          if (response.data?.success) {
            usedAI = true;
          } else {
            // AI returned a soft error — fall back to regex
            console.warn('AI parse failed softly, falling back to regex:', response.data?.error);
            response = null;
          }
        } catch (aiErr) {
          // Hard failure — log and fall back
          console.warn('AI parse hard-failed, falling back to regex:', aiErr?.response?.data?.error || aiErr.message);
          response = null;
        }
      }

      if (!response) {
        // Regex parse (legacy)
        response = await axios.post(
          `${API}/films/${filmId}/script/parse`,
          formData,
          { headers: getAuthHeaders() }
        );
      }
      
      if (response.data.success) {
        setParseResult(response.data);
        setAiUsed(usedAI);
        axios.get(`${API}/films/${filmId}/scenes/count`, { headers: getAuthHeaders() })
          .then((r) => setExistingCount(r.data?.count || 0))
          .catch(() => setExistingCount(0));
        // Initialize all scenes as selected (locked scenes stay unselected —
        // they carry no AI breakdown and act as upgrade CTAs)
        const initialSelected = {};
        response.data.scenes.forEach((scene, idx) => {
          initialSelected[idx] = !scene.locked;
        });
        setSelectedScenes(initialSelected);
      } else {
        setError(response.data.error || 'Failed to parse the script');
      }
    } catch (err) {
      if (err.response?.status === 413) {
        setError(`File is too large to upload. Please use a script file smaller than ${MAX_SCRIPT_FILE_SIZE_MB}MB.`);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to parse the script');
      }
    } finally {
      setParsing(false);
    }
  };

  const importScenes = async () => {
    if (!parseResult?.scenes) return;
    
    setImporting(true);
    setError(null);
    setImportResult(null);
    
    try {
      // Prepare scenes with selection status (locked scenes are never imported
      // with AI data — they act as upgrade CTAs only)
      const scenesToImport = parseResult.scenes.map((scene, idx) => ({
        ...scene,
        ai_model: parseResult.ai_model || undefined,   // so the stored breakdown records which model produced it
        selected: !!selectedScenes[idx] && !scene.locked
      }));
      
      const response = await axios.post(
        `${API}/films/${filmId}/script/import`,
        { scenes: scenesToImport },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setImportResult(response.data);
        if (response.data.created_count > 0 && onImportComplete) {
          onImportComplete();
        }
      } else {
        setError(response.data.error || 'Failed to import scenes');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to import scenes');
    } finally {
      setImporting(false);
    }
  };

  const toggleScene = (idx) => {
    const scene = parseResult?.scenes?.[idx];
    if (scene?.locked) {
      // Locked scenes route to the upgrade flow instead of toggling
      openUpgradeModal && openUpgradeModal('script_upload');
      return;
    }
    setSelectedScenes(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const togglePreviewExpand = (idx) => {
    setExpandedPreviews(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const selectAll = () => {
    if (!parseResult?.scenes) return;
    const newSelected = {};
    parseResult.scenes.forEach((scene, idx) => {
      newSelected[idx] = !scene.locked;
    });
    setSelectedScenes(newSelected);
  };

  const deselectAll = () => {
    setSelectedScenes({});
  };

  const selectedCount = Object.values(selectedScenes).filter(Boolean).length;

  /** M2.4: compare the parse with what's already on the film instead of importing duplicates. */
  const compareRevision = async () => {
    if (!parseResult?.scenes) return;
    setDiffing(true);
    setError(null);
    try {
      const scenes = parseResult.scenes.filter((sc) => !sc.locked).map((sc) => ({ ...sc, ai_model: parseResult.ai_model || undefined, selected: true }));
      const { data } = await axios.post(`${API}/films/${filmId}/script/diff`, { scenes }, { headers: getAuthHeaders() });
      setDiff({ ...data, scenes });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not compare with the existing scenes');
    } finally {
      setDiffing(false);
    }
  };

  const onRevisionApplied = (result) => {
    setRevisionResult(result);
    setDiff(null);
    if ((result.updated_count || result.created_count || result.deleted_count) && onImportComplete) onImportComplete();
  };

  const reset = () => {
    setFile(null);
    setParseResult(null);
    setImportResult(null);
    setDiff(null);
    setRevisionResult(null);
    setExistingCount(0);
    setError(null);
    setSelectedScenes({});
    setExpandedPreviews({});
    setViewingScene(null);
    setAiUsed(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatIntExt = (value) => {
    if (!value) return '-';
    return value === 'interior' ? 'INT' : value === 'exterior' ? 'EXT' : value.toUpperCase();
  };

  const formatTimeOfDay = (value) => {
    if (!value) return '-';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  return (
    <>
      <Card className="p-4 md:p-6" data-testid="script-import-section">
        {/* Header */}
        <div 
          className={`flex items-center justify-between cursor-pointer`}
          onClick={() => {
            if (disabled) {
              // Fail-closed UX: launch upgrade modal instead of silent tooltip
              openUpgradeModal && openUpgradeModal('script_upload');
            } else {
              setIsExpanded(!isExpanded);
            }
          }}
          data-testid="script-import-header"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center">
              <FileText className="h-5 w-5 text-foreground" />
            </div>
            <div>
              <h3 className="text-base md:text-lg font-semibold font-[Manrope]">Import from Script</h3>
              <p className="text-xs md:text-sm text-muted-foreground">
                {disabled
                  ? 'Upgrade to Pro to upload & auto-extract scenes from your screenplay'
                  : 'Upload a screenplay to auto-extract scenes with full text'}
              </p>
            </div>
          </div>
          {disabled ? (
            <div className="relative group flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  openUpgradeModal && openUpgradeModal('script_upload');
                }}
                className="opacity-80"
                data-testid="script-import-upgrade-button"
              >
                <Lock className="h-5 w-5" />
              </Button>
              <span className="absolute right-0 -top-9 hidden group-hover:inline-block bg-popover text-popover-foreground text-xs px-2 py-1.5 rounded shadow-md whitespace-nowrap z-50 border border-border">
                Upgrade to Pro to use this feature
              </span>
            </div>
          ) : (
            <Button variant="ghost" size="sm">
              {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </Button>
          )}
        </div>

        {/* Expandable Content */}
        {isExpanded && (
          <div className="mt-6 space-y-4" data-testid="script-import-content">
            {/* File Upload Area */}
            {!parseResult && !importResult && (
              <div
                className="border-2 border-dashed border-border rounded-lg p-6 md:p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
                data-testid="script-upload-dropzone"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,.txt,.docx,.fdx,.fountain"
                  className="hidden"
                  data-testid="script-file-input"
                />
                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm md:text-base font-medium mb-1">
                  {file ? file.name : 'Drop your script here or click to browse'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Supports PDF, DOCX, TXT, Final Draft (.fdx) and Fountain files
                </p>
              </div>
            )}

            {/* File Selected - Parse Button */}
            {file && !parseResult && !parsing && (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={reset}>
                      <X className="h-4 w-4" />
                    </Button>
                    <Button onClick={parseScript} data-testid="parse-script-button">
                      {useAI ? (
                        <>
                          <Sparkles className="h-4 w-4 mr-1.5" />
                          Parse with AI
                        </>
                      ) : (
                        'Parse Script'
                      )}
                    </Button>
                  </div>
                </div>

                {/* AI toggle */}
                <label
                  className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-chart-4/5 border border-chart-4/20 cursor-pointer hover:bg-chart-4/10 transition-colors"
                  data-testid="script-import-ai-toggle"
                >
                  <input
                    type="checkbox"
                    checked={useAI}
                    onChange={(e) => setUseAI(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-chart-4/40 text-chart-4 focus:ring-chart-4/30"
                    data-testid="script-import-ai-checkbox"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-foreground inline-flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-chart-4" />
                      Use AI Script Intelligence
                      <span className="text-[10px] font-semibold tracking-wider uppercase text-chart-4 bg-chart-4/15 px-1.5 py-0.5 rounded">
                        {freeSceneCap != null ? `First ${freeSceneCap} scenes free` : 'Pro'}
                      </span>
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Extract characters, props, key actions, and suggested shots for each scene.
                      {freeSceneCap != null
                        ? ` Your plan includes the full AI breakdown for the first ${freeSceneCap} scenes.`
                        : ''}
                      {' '}Falls back to the standard parser if AI is unavailable.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Parsing Loader */}
            {parsing && (
              <div className="flex flex-col items-center justify-center p-8 gap-3">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">
                    {useAI
                      ? 'Running AI Script Intelligence — extracting scenes, characters, props, and shot suggestions…'
                      : 'Parsing script and extracting scene text…'}
                  </span>
                </div>
                {useAI && (
                  <p className="text-[11px] text-muted-foreground">
                    This usually takes 10–30 seconds for a typical screenplay.
                  </p>
                )}
              </div>
            )}

            {/* Error Display */}
            {error && (
              <Alert variant="destructive" data-testid="script-import-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Warnings Display */}
            {parseResult?.warnings?.length > 0 && (
              <Alert data-testid="script-import-warnings">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {parseResult.warnings.map((w, i) => (
                    <p key={`warn-${i}-${w.slice(0, 24)}`}>{w}</p>
                  ))}
                </AlertDescription>
              </Alert>
            )}

            {/* M2.4: revision diff replaces the card list once requested */}
            {diff && !revisionResult && (
              <RevisionDiff
                filmId={filmId}
                scenes={diff.scenes}
                diff={diff}
                aiUsed={aiUsed}
                onApplied={onRevisionApplied}
                onCancel={() => setDiff(null)}
              />
            )}

            {revisionResult && (
              <div className="space-y-4" data-testid="revision-result">
                <Alert className="border-green-500/50 bg-green-500/10">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <AlertDescription className="text-green-700 dark:text-green-400">
                    Revision applied — {revisionResult.updated_count} updated, {revisionResult.created_count} added, {revisionResult.deleted_count} deleted.
                    {revisionResult.skipped_count > 0 && (
                      <span className="block mt-1 text-muted-foreground">{revisionResult.skipped_count} skipped: {revisionResult.skipped_scenes.join(', ')}</span>
                    )}
                  </AlertDescription>
                </Alert>
                <Button onClick={reset} className="w-full">Import Another Script</Button>
              </div>
            )}

            {/* Parse Results - Scene Preview Cards */}
            {parseResult?.scenes?.length > 0 && !importResult && !diff && !revisionResult && (
              <div className="space-y-4" data-testid="script-parse-results">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      {parseResult.total_detected} scenes detected
                      {aiUsed && (
                        <span className="text-[10px] font-semibold tracking-wider uppercase text-chart-4 bg-chart-4/15 px-2 py-0.5 rounded inline-flex items-center gap-1" data-testid="script-import-ai-badge">
                          <Sparkles className="h-2.5 w-2.5" /> AI
                        </span>
                      )}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {selectedCount} selected for import
                      {aiUsed
                        ? ' • Includes characters, props & suggested shots'
                        : ' • Scene text will be saved as description'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-scenes">
                      Select All
                    </Button>
                    <Button variant="outline" size="sm" onClick={deselectAll} data-testid="deselect-all-scenes">
                      Deselect All
                    </Button>
                  </div>
                </div>

                {/* Free-tier AI cap upgrade banner */}
                {parseResult.locked_count > 0 && (
                  <button
                    onClick={() => openUpgradeModal && openUpgradeModal('script_upload')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-chart-4/10 border border-chart-4/30 hover:bg-chart-4/15 transition-colors text-left"
                    data-testid="ai-cap-upgrade-banner"
                  >
                    <Lock className="h-4 w-4 text-chart-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {parseResult.free_scene_cap} of {parseResult.total_detected} scenes analyzed free
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {parseResult.locked_count} scene{parseResult.locked_count !== 1 ? 's' : ''} remaining — the Director plan continues from here.
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-chart-4 whitespace-nowrap">Upgrade →</span>
                  </button>
                )}

                {/* Scene Cards List */}
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                  {parseResult.scenes.map((scene, idx) => (
                    <div 
                      key={`scene-${scene.scene_number || idx}-${(scene.raw_slugline || '').slice(0, 32)}`}
                      className={`border rounded-lg transition-all ${
                        scene.locked
                          ? 'border-border bg-secondary/40 opacity-75'
                          : selectedScenes[idx] 
                          ? 'border-primary/50 bg-primary/5' 
                          : 'border-border bg-background'
                      }`}
                      data-testid={`scene-card-${idx}`}
                    >
                      {/* Scene Header Row */}
                      <div className="p-3 flex items-start gap-3">
                        {scene.locked ? (
                          <button
                            onClick={() => openUpgradeModal && openUpgradeModal('script_upload')}
                            className="mt-1 flex-shrink-0 text-muted-foreground hover:text-chart-4 transition-colors"
                            aria-label="Upgrade to unlock AI breakdown for this scene"
                            data-testid={`scene-locked-${idx}`}
                          >
                            <Lock className="h-4 w-4" />
                          </button>
                        ) : (
                          <Checkbox
                            checked={!!selectedScenes[idx]}
                            onCheckedChange={() => toggleScene(idx)}
                            className="mt-1"
                            data-testid={`scene-checkbox-${idx}`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          {/* Top row: Scene number and metadata */}
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-semibold text-primary">
                              {scene.scene_number}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 bg-secondary rounded">
                              {formatIntExt(scene.interior_exterior)}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 bg-secondary rounded">
                              {formatTimeOfDay(scene.time_of_day)}
                            </span>
                          </div>
                          
                          {/* Slugline */}
                          <p className="text-sm font-medium text-foreground truncate">
                            {scene.raw_slugline}
                          </p>
                          
                          {/* Location */}
                          {scene.location && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              📍 {scene.location}
                            </p>
                          )}

                          {/* Locked scene — upgrade CTA */}
                          {scene.locked && (
                            <button
                              onClick={() => openUpgradeModal && openUpgradeModal('script_upload')}
                              className="mt-2 flex items-center gap-1.5 text-[11px] text-chart-4 hover:underline text-left"
                              data-testid={`scene-locked-cta-${idx}`}
                            >
                              <Sparkles className="h-3 w-3 flex-shrink-0" />
                              {scene.locked_reason || 'Upgrade to unlock the full AI breakdown for this scene.'}
                            </button>
                          )}
                          
                          {/* Scene Text Preview */}
                          {scene.scene_text_preview && (
                            <div className="mt-2">
                              <p className="text-xs text-muted-foreground italic line-clamp-2">
                                {expandedPreviews[idx] ? scene.scene_text_preview : scene.scene_text_preview.slice(0, 100)}
                                {scene.scene_text_preview.length > 100 && !expandedPreviews[idx] && '...'}
                              </p>
                            </div>
                          )}

                          {/* AI-extracted chips — only when AI mode was used */}
                          {aiUsed && (scene.characters?.length || scene.props?.length || scene.suggested_shots?.length) ? (
                            <div className="mt-2.5 space-y-1.5" data-testid={`scene-ai-chips-${idx}`}>
                              {scene.characters?.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1">
                                  <Users className="h-3 w-3 text-primary flex-shrink-0" />
                                  {scene.characters.slice(0, 4).map((c, i) => (
                                    <span key={`c-${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                                      {c.name}
                                    </span>
                                  ))}
                                  {scene.characters.length > 4 && (
                                    <span className="text-[10px] text-muted-foreground">+{scene.characters.length - 4}</span>
                                  )}
                                </div>
                              )}
                              {scene.props?.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1">
                                  <Package className="h-3 w-3 text-warning flex-shrink-0" />
                                  {scene.props.slice(0, 4).map((p, i) => (
                                    <span key={`p-${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-warning/10 text-warning border border-warning/20">
                                      {p.name}
                                    </span>
                                  ))}
                                  {scene.props.length > 4 && (
                                    <span className="text-[10px] text-muted-foreground">+{scene.props.length - 4}</span>
                                  )}
                                </div>
                              )}
                              {scene.suggested_shots?.length > 0 && (
                                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                  <Camera className="h-3 w-3 text-chart-4" />
                                  <span>
                                    {scene.suggested_shots.length} AI-suggested shot{scene.suggested_shots.length !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                        
                        {/* View Full Text Button */}
                        {scene.scene_text && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingScene(scene);
                            }}
                            className="flex-shrink-0"
                            data-testid={`view-scene-text-${idx}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">View</span>
                          </Button>
                        )}
                      </div>
                      
                      {/* Expandable Scene Text Section */}
                      {scene.scene_text && (
                        <div 
                          className="border-t border-border"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => togglePreviewExpand(idx)}
                            className="w-full px-3 py-2 flex items-center justify-between text-xs text-muted-foreground hover:bg-secondary/50 transition-colors"
                            data-testid={`toggle-preview-${idx}`}
                          >
                            <span>
                              {expandedPreviews[idx] ? 'Hide preview' : 'Show preview'}
                            </span>
                            <ChevronRight className={`h-3 w-3 transition-transform ${expandedPreviews[idx] ? 'rotate-90' : ''}`} />
                          </button>
                          
                          {expandedPreviews[idx] && (
                            <div className="px-3 pb-3">
                              <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-sans bg-secondary/30 rounded p-2 max-h-32 overflow-y-auto">
                                {scene.scene_text.slice(0, 500)}
                                {scene.scene_text.length > 500 && '...'}
                              </pre>
                              {scene.scene_text.length > 500 && (
                                <p className="text-xs text-muted-foreground mt-1 text-center">
                                  <button 
                                    onClick={() => setViewingScene(scene)}
                                    className="text-primary hover:underline"
                                  >
                                    View full scene text ({scene.scene_text.length} characters)
                                  </button>
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Revision hint: the film already has scenes */}
                {existingCount > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2" data-testid="revision-hint">
                    <p className="text-xs flex-1">
                      <strong>Revised draft?</strong> This film already has {existingCount} scene{existingCount !== 1 ? 's' : ''}. Compare first to update
                      what changed and keep your shots, cast and schedule — importing adds duplicates.
                    </p>
                    <Button size="sm" variant="secondary" onClick={compareRevision} disabled={diffing} data-testid="compare-revision-button">
                      {diffing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <GitCompareArrows className="h-4 w-4 mr-1" />}
                      Compare with existing scenes
                    </Button>
                  </div>
                )}

                {/* Import Actions */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <Button variant="outline" onClick={reset} data-testid="cancel-import-button">
                    Cancel
                  </Button>
                  <Button 
                    onClick={importScenes} 
                    disabled={selectedCount === 0 || importing}
                    variant={existingCount > 0 ? 'outline' : 'default'}
                    data-testid="import-scenes-button"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        Import {selectedCount} Scene{selectedCount !== 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Import Success Result */}
            {importResult && (
              <div className="space-y-4" data-testid="import-result">
                <Alert className="border-green-500/50 bg-green-500/10">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <AlertDescription className="text-green-700 dark:text-green-400">
                    Successfully imported {importResult.created_count} scene{importResult.created_count !== 1 ? 's' : ''} with full scene text!
                    {aiUsed && freeSceneCap == null && (
                      <span className="block mt-1 text-foreground">
                        Open <strong>AI Script Analysis</strong> from the film dashboard to see characters, props,
                        key actions, and add the suggested shots to your shot list.
                      </span>
                    )}
                    {aiUsed && freeSceneCap != null && (
                      <span className="block mt-1 text-foreground">
                        Your first {freeSceneCap} scenes came with a full AI breakdown. Upgrade to Director to
                        re-analyze scenes and generate shot lists any time.
                      </span>
                    )}
                    {importResult.skipped_count > 0 && (
                      <span className="block mt-1 text-muted-foreground">
                        {importResult.skipped_count} scene{importResult.skipped_count !== 1 ? 's' : ''} skipped
                      </span>
                    )}
                  </AlertDescription>
                </Alert>

                {/* Skipped Scenes Details */}
                {importResult.skipped_scenes?.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium mb-1">Skipped scenes:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {importResult.skipped_scenes.map((reason, idx) => (
                        <li key={`skip-${idx}-${(reason || '').slice(0, 32)}`}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <Button onClick={reset} className="w-full" data-testid="import-another-button">
                  Import Another Script
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Full Scene Text Modal */}
      <Dialog open={!!viewingScene} onOpenChange={() => setViewingScene(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]" aria-describedby="scene-text-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="font-mono text-primary">{viewingScene?.scene_number}</span>
              <span className="text-muted-foreground">•</span>
              <span className="truncate">{viewingScene?.location || viewingScene?.scene_name}</span>
            </DialogTitle>
          </DialogHeader>
          
          {/* Scene Metadata */}
          <div className="flex flex-wrap gap-2 mb-3" id="scene-text-description">
            <span className="text-xs px-2 py-1 bg-secondary rounded">
              {formatIntExt(viewingScene?.interior_exterior)}
            </span>
            <span className="text-xs px-2 py-1 bg-secondary rounded">
              {formatTimeOfDay(viewingScene?.time_of_day)}
            </span>
          </div>
          
          {/* Slugline */}
          <div className="text-sm font-medium text-foreground mb-3 p-2 bg-secondary/50 rounded">
            {viewingScene?.raw_slugline}
          </div>
          
          {/* Full Scene Text */}
          <ScrollArea className="h-[400px] rounded border p-4">
            <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">
              {viewingScene?.scene_text || 'No scene text extracted.'}
            </pre>
          </ScrollArea>
          
          {/* Character Count */}
          <div className="text-xs text-muted-foreground text-right">
            {viewingScene?.scene_text?.length || 0} characters
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScriptImport;
